import Dexie, { Transaction } from "dexie";
import { fs } from "../firebase.ts";
import {
    Transcription,
    Transcription2,
    Transcription3
} from "../models/Transcription.ts";
import { version } from "../../package.json";

import {
    collection,
    doc,
    getDocs,
    updateDoc,
    query,
    where,
    CollectionReference,
    DocumentData,
    onSnapshot,
    getDoc
} from "firebase/firestore";

//TODO: check if user is logged in first??
export class Transcriptions extends Dexie {
    private transcriptions: Dexie.Table<Transcription, number>;
    private collection: CollectionReference<DocumentData>;
    private unsubscribeKhmer?: () => void;
    private unsubscribePali?: () => void;

    constructor() {
        super("Transcriptions");
        this.version(parseInt(version))
            .stores({
                transcriptions:
                    "++id, id2, book, page, language, [book+page+language]"
            })
            .upgrade((tx: Transaction) => {
                // Access the 'transcriptions' table
                const transcriptionsTable = tx.table("transcriptions");

                // Example 1: Delete a specific record by primary key (id)
                //return transcriptionsTable.delete(1); // Deletes the record with id = 1

                // Example 2: Delete records matching a condition
                // return transcriptionsTable
                //     .where('book')
                //     .equals('someBook')
                //     .delete(); // Deletes all records where book = 'someBook'

                // Example 3: Clear the entire table
                //return transcriptionsTable.clear(); // Deletes all records in the table
            });
        this.transcriptions = this.table("transcriptions");
        this.collection = collection(fs, "transcriptions");
    }

    // can also update by dexie id ??
    async getByID(id: string): Promise<Transcription | null> {
        return (
            (await this.transcriptions.where("id2").equals(id).first()) || null
        );
    }

    // deprecated
    //TODO: always call firestore so dexie becomes obsolete! // do as drafts instead!
    // later: just get metadata like updateTime via cloud function; maybe cheaper than firestore reads?
    async checkForUpdate(id: string): Promise<Transcription2 | null> {
        const transcription = (await this.getByID(id)) as Transcription;
        const docRef = doc(fs, "transcriptions", id);
        const docSnapshot = await getDoc(docRef);
        const exists = docSnapshot.exists(); // deleted ?
        const data = { id, ...docSnapshot.data() } as Transcription2;

        if (data.editedDate !== transcription.date) {
            this.update(
                id,
                data.editedDate,
                data.book,
                data.page,
                data.language,
                data.transcript,
                data.translation
            );
            return data;
        } else return null;
    }

    // deprecated
    async get(
        book: number,
        page: number,
        language: string
    ): Promise<Transcription2 | null> {
        const transcription = (await this.transcriptions
            .where("[book+page+language]")
            .equals([book, page, language])
            .first()) as Transcription;

        if (transcription) {
            const transcription2 = this.checkForUpdate(transcription.id2!);
            if (transcription2) {
                return transcription2;
            } else {
                return {
                    id: transcription.id2!,
                    uid: null,
                    editedDate: transcription.date,
                    book: transcription.book,
                    page: transcription.page,
                    language: transcription.language,
                    transcript: transcription.transcript,
                    translation: transcription.translation,
                    audio: null
                };
            }
        } else {
            return null;
        }
    }

    async update(
        id: string,
        date: Date | null,
        book: number,
        page: number,
        language: string,
        transcript: string,
        translation: string
    ): Promise<number> {
        const transcription = await this.getByID(id);
        if (transcription) {
            transcription.date = date;
            if (transcript) transcription.transcript = transcript;
            if (translation) transcription.translation = translation;
            return this.transcriptions.update(transcription.id!, transcription);
        } else {
            return this.transcriptions.add({
                id2: id,
                date,
                book,
                page,
                language,
                transcript,
                translation
            });
        }
    }

    // returns number of deleted items = should be 2
    async remove(book: number, page: number): Promise<number> {
        return this.transcriptions
            .where("[book+page]")
            .equals([book, page])
            .delete();
    }

    // firestore
    // weird bug: double entry: one with editedDate null and one without... could be related to past refactor...

    async getTranscription(
        book: number,
        page: number,
        language: string
    ): Promise<Transcription2 | null> {
        const q = query(
            this.collection,
            where("book", "==", book),
            where("page", "==", page),
            where("language", "==", language)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const docSnap = querySnapshot.docs[0];
            const docData = docSnap.data();
            return {
                id: docSnap.id,
                ...docData
            } as Transcription2;
        } else {
            return null; //TODO: create new doc ??? maybe // can bypass cloud function; but need to update firestore rule // if running out of quota for cloud function
        }
    }

    async updateTranscript(
        id: string,
        uid: string,
        transcript: string
    ): Promise<string> {
        try {
            const docRef = doc(fs, "transcriptions", id);
            await updateDoc(docRef, {
                transcript,
                editedDate: new Date()
            });
            return "success";
        } catch (error: any) {
            console.error("Error updating document: ", error.message);
            return "error";
        }
    }

    async updateTranslation(
        id: string,
        uid: string,
        translation: string
    ): Promise<string> {
        try {
            const docRef = doc(fs, "transcriptions", id);
            await updateDoc(docRef, {
                translation,
                editedDate: new Date()
            });
            return "success";
        } catch (error: any) {
            console.error("Error updating document: ", error.message);
            return "error";
        }
    }

    async updateAudio(id: string, uid: string, audio: string): Promise<string> {
        try {
            const docRef = doc(fs, "transcriptions", id);
            await updateDoc(docRef, {
                audio
            });
            return "success";
        } catch (error: any) {
            console.error("Error updating audio: ", error.message);
            return "error";
        }
    }

    setupRealtimeListener(
        element: "pali" | "khmer",
        id: string,
        language: string, // same as LANGUAGE
        callback: Transcription3
    ) {
        // Reference to single document
        const docRef = doc(fs, "transcriptions", id);

        // Clean up existing listener if it exists
        if (element === "pali") {
            if (this.unsubscribePali) this.unsubscribePali();
        } else {
            if (this.unsubscribeKhmer) this.unsubscribeKhmer();
        }

        // Listen to single document changes
        const unsubscribe = onSnapshot(
            docRef,
            (docSnap) => {
                if (docSnap.exists()) {
                    const transcription = {
                        id: docSnap.id,
                        ...docSnap.data()
                    } as Transcription2;
                    // this.update(
                    //     transcription.id,
                    //     transcription.editedDate,
                    //     transcription.book,
                    //     transcription.page,
                    //     transcription.language,
                    //     transcription.transcript || "",
                    //     transcription.translation || ""
                    // );
                    callback(
                        element,
                        transcription.id,
                        transcription.editedDate,
                        transcription.transcript,
                        language === "khmer"
                            ? transcription.transcript
                            : transcription.translation,
                        !!transcription.transcript,
                        transcription.audio
                    );
                    // (document.querySelector("sw-khmer") as any).render(
                    //     language === "khmer"
                    //         ? transcription.transcript
                    //         : transcription.translation
                    // );
                }
            },
            (error) => {
                console.error("Error listening to document: ", error.message);
            }
        );

        if (element === "pali") this.unsubscribePali = unsubscribe;
        else this.unsubscribeKhmer = unsubscribe;
    }
}
